import { addDays } from '../accounting.js';
export const NEW_CARD = { ease: 250, intervalDays: 0, reps: 0, lapses: 0 };
const MIN_EASE = 130;
const MAX_INTERVAL = 365;
/**
 * The next state after one review.
 *
 * "Again" resets the interval and costs ease; a card that keeps being
 * forgotten therefore comes back more and more often, which is the point.
 * "Hard" grows the interval only slightly and trims ease; "good" follows
 * the standard 1 → 6 → ×ease progression; "easy" adds a bonus on top.
 */
export function schedule(card, grade, today) {
    if (grade === 0) {
        return {
            ease: Math.max(MIN_EASE, card.ease - 20),
            intervalDays: 0,
            reps: 0,
            lapses: card.lapses + 1,
            dueOn: today,
        };
    }
    const ease = Math.max(MIN_EASE, card.ease + (grade === 1 ? -15 : grade === 3 ? 15 : 0));
    let interval;
    if (card.reps === 0)
        interval = 1;
    else if (card.reps === 1)
        interval = 6;
    else
        interval = Math.round((card.intervalDays * ease) / 100);
    if (grade === 1)
        interval = Math.max(1, Math.round(interval * 0.6));
    if (grade === 3)
        interval = Math.round(interval * 1.3);
    interval = Math.min(MAX_INTERVAL, Math.max(1, interval));
    return {
        ease,
        intervalDays: interval,
        reps: card.reps + 1,
        lapses: card.lapses,
        dueOn: addDays(today, interval),
    };
}
/** Cards to show in one sitting: everything due, oldest first, capped. */
export function pickDue(cards, today, limit = 30) {
    return cards
        .filter((c) => c.dueOn <= today)
        .sort((a, b) => (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : 0))
        .slice(0, limit);
}
/* ── the day streak ─────────────────────────────────────────────────────
 * Days in a row with any study activity.
 *
 * Two deliberate softenings, both for adults who paid for a course and
 * abandon tools that punish them:
 *
 *  - Today does not have to be done yet. A streak that reads "0" at 8 a.m.
 *    because you have not studied since midnight is a streak nobody keeps.
 *  - One missed day costs one point instead of everything. A single bad day
 *    steps the count back; two in a row end it.
 */
/**
 * `days` are the distinct 'YYYY-MM-DD' dates with activity, in any order.
 * Returns the streak as of `today`.
 */
export function streak(days, today) {
    const has = new Set(days);
    // Start at today if it has activity, else yesterday — the day is not over.
    let cursor = has.has(today) ? today : addDays(today, -1);
    let count = 0;
    let gapUsed = false;
    for (let guard = 0; guard < 400; guard++) {
        if (has.has(cursor)) {
            count += 1;
            cursor = addDays(cursor, -1);
            continue;
        }
        // A single missed day, with the run continuing behind it: bridge it at a cost.
        if (!gapUsed && count > 0 && has.has(addDays(cursor, -1))) {
            gapUsed = true;
            count -= 1;
            cursor = addDays(cursor, -1);
            continue;
        }
        break;
    }
    return Math.max(0, count);
}
